import { db, auth, storage } from "@/lib/firebase/clientApp";
import {
  addDoc,
  serverTimestamp,
  updateDoc,
  doc,
  query,
  orderBy,
  getDocs,
  getDoc,
  where,
  collection,
  deleteDoc,
} from "firebase/firestore";
import { ref as storageRef, listAll } from "firebase/storage";
import {
  ProjectDocument,
  ProjectUploadPayload,
} from "@/app/(workspace)/_types/project";
import { STORAGE_UPLOADS_PATH, deleteFile } from "./storage";
import {
  canCreateProject,
  incrementProjectUsage,
  getUserSubscription,
  checkFileSizeLimit,
  subscriptionsCollection,
} from "./subscription";

export const jobsCollection = collection(
  db,
  process.env.NEXT_PUBLIC_FIREBASE_JOBS_COLLECTION!
);

export async function getProjects(): Promise<ProjectDocument[]> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  // 사용자의 프로젝트 가져오기
  const userProjectsSnapshot = await getDocs(
    query(
      jobsCollection,
      orderBy("createdAt", "desc"),
      where("status", "!=", "ARCHIVED"),
      where("user.id", "==", auth.currentUser.uid)
    )
  );

  // 샘플 프로젝트 가져오기
  const sampleProjectsSnapshot = await getDocs(
    query(
      jobsCollection,
      orderBy("createdAt", "desc"),
      where("status", "!=", "ARCHIVED"),
      where("sample", "==", true)
    )
  );

  // 두 결과를 병합 (중복 제거를 위해 Map 사용)
  const projectsMap = new Map<string, ProjectDocument>();

  // 일반 잡만 list에 표시. lod_backfill 같은 보조 잡은 사용자 카드 list에 노출 안 함
  // (file 필드 없음 + 내부 작업이라 사용자가 볼 필요 없음)
  const isUserFacingJob = (data: any) => data?.type !== "lod_backfill";

  // 사용자 프로젝트 추가
  userProjectsSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (!isUserFacingJob(data)) return;
    projectsMap.set(doc.id, {
      id: doc.id,
      ...data,
    } as ProjectDocument);
  });

  // 샘플 프로젝트 추가 (본인 프로젝트는 이미 포함되어 있으므로 중복 제거됨)
  sampleProjectsSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (!isUserFacingJob(data)) return;
    if (!projectsMap.has(doc.id)) {
      projectsMap.set(doc.id, {
        id: doc.id,
        ...data,
      } as ProjectDocument);
    }
  });

  // Map을 배열로 변환하고 이중 정렬
  // 1순위: 사용자 프로젝트(sample=false)가 샘플 프로젝트(sample=true)보다 먼저
  // 2순위: 각 그룹 내에서 createdAt 내림차순 정렬
  const docs = Array.from(projectsMap.values()).sort((a, b) => {
    const aSample = a.sample || false;
    const bSample = b.sample || false;

    // 샘플 여부가 다르면 사용자 프로젝트를 먼저 표시
    if (aSample !== bSample) {
      return aSample ? 1 : -1; // false(사용자) < true(샘플)
    }

    // 같은 그룹 내에서는 최신순으로 정렬
    const aTime = a.createdAt?.toMillis() || 0;
    const bTime = b.createdAt?.toMillis() || 0;
    return bTime - aTime; // 내림차순 정렬
  });

  return docs;
}

export async function getAllProjects(): Promise<ProjectDocument[]> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  // 모든 프로젝트 가져오기 (ARCHIVED 포함). lod_backfill 같은 보조 잡은 카드 list 비대상
  const allProjectsSnapshot = await getDocs(
    query(jobsCollection, orderBy("createdAt", "desc"))
  );
  const userFacingDocs = allProjectsSnapshot.docs.filter(
    (d) => d.data()?.type !== "lod_backfill"
  );

  // 고유한 사용자 ID 목록 추출
  const userIds = [...new Set(userFacingDocs.map(doc => doc.data().user?.id).filter(Boolean))];

  // 사용자별 구독 정보 가져오기
  const subscriptionMap = new Map<string, string | null>();
  if (userIds.length > 0) {
    const subscriptionSnapshots = await Promise.all(
      userIds.map(userId => getDoc(doc(subscriptionsCollection, userId)))
    );
    subscriptionSnapshots.forEach((snap, index) => {
      if (snap.exists()) {
        subscriptionMap.set(userIds[index], snap.data()?.tier || null);
      } else {
        subscriptionMap.set(userIds[index], null);
      }
    });
  }

  const docs = userFacingDocs.map((docSnap) => {
    const data = docSnap.data();
    const userId = data.user?.id;
    return {
      id: docSnap.id,
      ...data,
      user: {
        ...data.user,
        plan: userId ? subscriptionMap.get(userId) || null : null,
      },
    } as ProjectDocument;
  });

  return docs;
}

export async function getProject(projectId: string): Promise<ProjectDocument | null> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  const docRef = doc(jobsCollection, projectId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
  } as ProjectDocument;
}

export async function createDoc(project: ProjectUploadPayload) {
  const { file, title, options } = project;

  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  // 구독 제한 확인
  const createPermission = await canCreateProject(auth.currentUser.uid);
  if (!createPermission.canCreate) {
    throw new Error(createPermission.reason || "프로젝트를 생성할 수 없습니다.");
  }

  // 파일 크기 제한 확인
  const subscription = await getUserSubscription(auth.currentUser.uid);
  if (subscription) {
    const fileSizeMB = file.size / (1024 * 1024); // bytes to MB
    const fileSizeCheck = checkFileSizeLimit(subscription, fileSizeMB);
    if (!fileSizeCheck.isValid) {
      throw new Error(fileSizeCheck.reason || "파일 크기가 제한을 초과합니다.");
    }
  }

  const now = serverTimestamp();

  try {
    const docData = {
      createdAt: now,
      title: title,
      file: {
        originalFilename: file.name,
        filename: "",
        storagePath: "",
        size: file.size,
      },
      options: {
        quality: options.quality,
        sceneSize: options.sceneSize,
        autoColorCorrection: options.autoColorCorrection,
        ...(options.worldDensity && { worldDensity: options.worldDensity }),
        ...(options.lodProfile && { lodProfile: options.lodProfile }),
        ...(options.useExtSplats !== undefined && {
          useExtSplats: options.useExtSplats,
        }),
      },
      progress: 0,
      result: {
        success: false,
      },
      status: "UPLOADING",
      updatedAt: now,
      user: {
        id: auth.currentUser.uid,
        username: auth.currentUser.displayName,
        email: auth.currentUser.email,
      },
    };
    const data = await addDoc(jobsCollection, docData);

    // 사용량 업데이트
    await incrementProjectUsage(auth.currentUser.uid, data.id);

    return data.id;
  } catch (error) {
    console.error("[createDoc] Error:", error);
    throw error;
  }
}

export async function updateDocStatus(docId: string, status: string) {
  const ref = doc(jobsCollection, docId);
  await updateDoc(ref, { status, updatedAt: serverTimestamp() });
}

export async function completeUpload(docId: string, uuid: string) {
  const ref = doc(jobsCollection, docId);
  await updateDoc(ref, {
    status: "PENDING",
    "file.filename": uuid,
    "file.storagePath": STORAGE_UPLOADS_PATH,
    updatedAt: serverTimestamp(),
  });
}

export async function updateProjectTitle(projectId: string, newTitle: string): Promise<void> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  const project = await getProject(projectId);
  if (!project) {
    throw new Error("Project not found");
  }

  // 본인 프로젝트인지 확인
  if (project.user.id !== auth.currentUser.uid) {
    throw new Error("You can only edit your own projects");
  }

  const ref = doc(jobsCollection, projectId);
  await updateDoc(ref, {
    title: newTitle.trim(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteProjectAdmin(projectId: string): Promise<string[]> {
  if (!auth.currentUser) {
    throw new Error("User not authenticated");
  }

  const project = await getProject(projectId);
  if (!project) {
    throw new Error("Project not found");
  }

  const deletedFiles: string[] = [];
  const filesToDelete: string[] = [];

  // 1. 원본 파일
  if (project.file.storagePath && project.file.filename) {
    filesToDelete.push(`${project.file.storagePath}/${project.file.filename}`);
  }

  // 2. 결과 파일 (PLY) — splatx-worker 서브폴더 레이아웃: "{jid}/{jid}.ply"
  if (project.result?.ply) {
    filesToDelete.push(`job/results/${project.result.ply}`);
  }

  // 3. 결과 파일 (SPZ) — splatx-worker 서브폴더 레이아웃: "{jid}/{jid}.spz"
  if (project.result?.spz) {
    filesToDelete.push(`job/results/${project.result.spz}`);
  }

  // 2b/3b. Flat layout 폴백 — 마이그레이션 이전 완료된 구 프로젝트 대응
  //   (result.ply 값이 "{jid}.ply"인 경우 위에서 처리됐지만,
  //    result 필드 업데이트 누락으로 flat 경로에만 파일이 남아있는 케이스 방어)
  //   deleteFile은 미존재를 graceful 처리하므로 중복 시도해도 안전.
  filesToDelete.push(`job/results/${projectId}.ply`);
  filesToDelete.push(`job/results/${projectId}.spz`);

  // 4. LoD 폴더 — listAll로 .rad / .radc 일괄 조회
  //    청크 개수가 가변(수~수백 개)이라 chunkCount 의존 대신 실제 폴더 리스팅 사용
  try {
    const lodFolderRef = storageRef(storage, `job/results/${projectId}/lod`);
    const { items } = await listAll(lodFolderRef);
    items.forEach((item) => filesToDelete.push(item.fullPath));
  } catch (error) {
    console.warn(`Failed to list lod folder for ${projectId}:`, error);
  }

  // 5. 썸네일 — 경로 변경 없음
  filesToDelete.push(`job/thumbnails/${projectId}.jpg`);

  // 파일 삭제 실행
  await Promise.all(
    filesToDelete.map(async (path) => {
      try {
        const deleted = await deleteFile(path);
        if (deleted) {
          deletedFiles.push(path);
        }
      } catch (error) {
        console.warn(`Failed to delete file: ${path}`, error);
      }
    })
  );

  // Firestore 문서 삭제
  const ref = doc(jobsCollection, projectId);
  await deleteDoc(ref);

  return deletedFiles;
}
